import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { SnFormFieldConfig } from '@lib/form';
import { t } from '@lib/translation';

import { LprHotListAPI, SnLpr, SnLprHotListGroup, SnLprObject } from '../../api';
import { SnLprFieldsGeneratorService } from '../../services';

@Component({
  templateUrl: './add-plate-to-group-dialog.component.html',
  styleUrls: ['./add-plate-to-group-dialog.component.scss'],
})
export class AddPlateToGroupDialogComponent implements OnInit, OnDestroy {
  public form = new UntypedFormGroup({});
  public formFields: SnFormFieldConfig[] = [];
  public lprOptions = [];

  public formModel: any = {};
  public isLoading: boolean = false;
  public isFetchingGroups: boolean = false;
  public groups = [];
  private readonly _lprGroups$ = new Subject<any[]>();
  private readonly _destroy$ = new Subject();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      group: SnLprHotListGroup;
      list: SnLpr;
      item?: SnLprObject;
    },
    private readonly _dialogRef: MatDialogRef<AddPlateToGroupDialogComponent>,
    private readonly _lprFieldsGeneratorService: SnLprFieldsGeneratorService,
    private readonly _lprHotlistAPI: LprHotListAPI,
  ) {}

  get lprGroups$(): Observable<any[]> {
    return this._lprGroups$.asObservable();
  }

  public ngOnInit(): void {
    this.formModel.group = this.data.group.id;
    if (this.data.item) {
      this.formModel = {
        ...this.data.item,
        ...this.data.item.properties,
      };
    }
    this._setForm();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public submitForm(): void {
    this.isLoading = true;
    if (this.formModel.id) {
      this._lprHotlistAPI
        .updateLprObject(this.formModel.id, {
          group: this.formModel.group,
          plate: this.form.get('plate').value,
          properties: {
            reason:
              this.formModel.reason !== 'other'
                ? this.formModel.reason
                : this.formModel.reason_text,
            list: this.data.list.name,
            description: this.formModel.description,
          },
          expiresTimestamp: this.formModel.expiresTimestamp,
          lprHotlist: this.data.list.id,
        })
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (response) => {
            this.isLoading = false;
            this._dialogRef.close(response);
          },
          error: () => {
            this.isLoading = false;
            this._dialogRef.close();
          },
        });
    } else {
      this._lprHotlistAPI
        .addPlateToLprObject({
          plate: this.form.get('plate').value,
          group: this.data.group.id,
          properties: {
            reason:
              this.formModel.reason !== 'other'
                ? this.formModel.reason
                : this.formModel.reason_text,
            list: this.data.list.name,
            description: this.formModel.description,
          },
          expiresTimestamp: this.formModel.expiresTimestamp,
          lprHotlist: this.data.list.id,
        })
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          next: (response) => {
            this.isLoading = false;
            this._dialogRef.close(response);
          },
          error: () => {
            this.isLoading = false;
            this._dialogRef.close();
          },
        });
    }
  }

  private _setForm(): void {
    this.formFields = [
      this._lprFieldsGeneratorService.addPlateField({
        key: 'plate',
        type: 'input',
        templateOptions: {
          label: t('vehicleIntelligence.hotlist.groups.tag'),
          placeholder: t('vehicleIntelligence.hotlist.groups.enterTag'),
          required: true,
        },
      }),
      {
        key: 'reason',
        type: 'select',
        templateOptions: {
          label: t('settings.demoPanel.alprAlert.reason'),
          required: true,
          floatLabel: 'always',
          placeholder: t('settings.demoPanel.alprAlert.selectReason'),
          valueProp: 'value',
          labelProp: 'label',
          options: [
            { label: t('settings.demoPanel.alprAlert.stolen'), value: 'stolen' },
            { label: t('settings.demoPanel.alprAlert.expired'), value: 'expired' },
            { label: t('settings.demoPanel.alprAlert.suspended'), value: 'suspended' },
            { label: t('common.other'), value: 'other' },
          ],
        },
      },
      {
        key: 'reason_text',
        type: 'input',
        templateOptions: {
          label: t('vehicleIntelligence.hotlist.groups.otherReason'),
        },
        hideExpression: () => this.formModel.reason !== 'other',
      },
      {
        key: 'description',
        type: 'input',
        templateOptions: {
          label: t('common.description'),
        },
      },
      {
        key: 'expiresTimestamp',
        type: 'datetimepicker',
        templateOptions: {
          label: t('common.expirationDate'),
          appearance: 'fill',
        },
      },
    ];
  }
}
